import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
  MenuItem, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Edit, PhotoCamera, Timeline } from '@mui/icons-material';
import api from '../../services/api';
import type { FactoryMaterial, LotTrace, Machine, MaterialLot, MaterialOrigin, MixBatch, MixRecipe, MixTrace, Reception, ScaleReadResult, TraceShift } from '../../types/factory';
import { EmptyState, KpiStrip, PageHeader, ProductSelect, Section, StatusPill, addDaysIso, compressPhoto, errorMessage, fmtQty, num, todayIso, usePageView } from './shared';
import type { PageView } from './shared';
import { KpiItem, Tone } from '../../components/ui/PageKit';

// ==================== Origen de la MP ====================

const ORIGINS: { value: MaterialOrigin; label: string; color: string; tone: Tone }[] = [
  { value: 'Virgen', label: 'Virgen', color: '#384DF4', tone: 'info' },
  { value: 'RecuperadaPropia', label: 'Recuperada propia', color: '#16A36A', tone: 'ok' },
  { value: 'RecuperadaComprada', label: 'Recuperada comprada', color: '#E59A0C', tone: 'warn' },
];
const NO_ORIGIN_COLOR = '#B8BDC6';
const originOf = (o?: string | null) => ORIGINS.find(x => x.value === o);

const OriginPill: React.FC<{ origin?: string | null }> = ({ origin }) => {
  const o = originOf(origin);
  return o ? <StatusPill tone={o.tone}>{o.label}</StatusPill> : <StatusPill tone="neutral">Sin origen</StatusPill>;
};

/** Barra de composición de una mezcla por origen (virgen / recuperada). */
const CompositionBar: React.FC<{ parts: { origin?: string | null; kg: number }[]; height?: number }> = ({ parts, height = 8 }) => {
  const total = parts.reduce((a, p) => a + p.kg, 0) || 1;
  const byOrigin = new Map<string, number>();
  parts.forEach(p => byOrigin.set(p.origin || '', (byOrigin.get(p.origin || '') || 0) + p.kg));
  const segs = [...ORIGINS.map(o => ({ key: o.value, label: o.label, color: o.color })), { key: '', label: 'Sin origen', color: NO_ORIGIN_COLOR }]
    .map(s => ({ ...s, kg: byOrigin.get(s.key) || 0 })).filter(s => s.kg > 0);
  return (
    <Tooltip title={segs.map(s => `${s.label} ${Math.round((s.kg / total) * 100)}%`).join(' · ')}>
      <Box sx={{ display: 'flex', height, borderRadius: 999, overflow: 'hidden', bgcolor: '#EEE', minWidth: 120, gap: '2px' }}>
        {segs.map(s => <Box key={s.key} sx={{ width: `${(s.kg / total) * 100}%`, bgcolor: s.color }} />)}
      </Box>
    </Tooltip>
  );
};

// ==================== Fechas y fotos ====================

const AR = 'America/Argentina/Buenos_Aires';
const fmtDateTime = (s?: string | null) => (s ? new Date(s).toLocaleString('es-AR', { timeZone: AR, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const fmtTime = (s?: string | null) => (s ? new Date(s).toLocaleTimeString('es-AR', { timeZone: AR, hour: '2-digit', minute: '2-digit' }) : '—');
const fmtDay = (s?: string | null) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(2, 4)}` : '—');
const fmtKg = (n?: number | null) => (n == null ? '—' : `${fmtQty(n)} kg`);


const photoCache = new Map<number, Promise<string>>();
function usePhoto(id?: number | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!id) { setUrl(null); return; }
    if (!photoCache.has(id)) photoCache.set(id, api.getFactoryPhoto(id).then(b => URL.createObjectURL(b)));
    let alive = true;
    photoCache.get(id)!.then(u => alive && setUrl(u)).catch(() => photoCache.delete(id));
    return () => { alive = false; };
  }, [id]);
  return url;
}

const PhotoThumb: React.FC<{ id?: number | null; size?: number }> = ({ id, size = 40 }) => {
  const url = usePhoto(id);
  const [open, setOpen] = useState(false);
  if (!id) return <Typography variant="caption" color="text.secondary">sin foto</Typography>;
  return (
    <>
      <Box component="button" type="button" onClick={() => setOpen(true)} aria-label="Ver foto del visor"
        sx={{ p: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, width: size, height: size, overflow: 'hidden', cursor: 'zoom-in', bgcolor: '#F0F0EE', display: 'block' }}>
        {url && <Box component="img" src={url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md">
        {url && <Box component="img" src={url} alt="Foto del visor" sx={{ display: 'block', maxWidth: '100%', maxHeight: '80vh' }} />}
      </Dialog>
    </>
  );
};

// ==================== Página ====================

const VIEWS: PageView[] = [
  { key: 'recepcion', label: 'Recepción de MP', description: 'Lo que entra se pesa (balanza, foto del visor o a mano) y queda como lote de materia prima.' },
  { key: 'mezclas', label: 'Mezclas', description: 'Cada tanda de mezcla consume lotes de MP (del más viejo al más nuevo) y es lo que se carga en la inyectora.' },
  { key: 'lotes', label: 'Lotes de MP', description: 'Cada ingreso de materia prima, lo que le queda y en qué mezclas se usó.' },
  { key: 'recetas', label: 'Recetas de mezcla y orígenes de MP', description: 'Qué lleva cada mezcla y de dónde viene cada materia prima (virgen o recuperada).' },
];

const FactoryMaterialsPage: React.FC = () => {
  const { tab, setTab, showTabs, flat, current } = usePageView(VIEWS);
  const [materials, setMaterials] = useState<FactoryMaterial[]>([]);
  const [trace, setTrace] = useState<{ kind: 'mix' | 'lot'; id: number } | null>(null);
  const loadMaterials = useCallback(async () => setMaterials(await api.getFactoryMaterials()), []);
  useEffect(() => { loadMaterials().catch(() => setMaterials([])); }, [loadMaterials]);

  return (
    <Box>
      <PageHeader
        title={flat ? current.label : 'Materia prima'}
        description={flat && current.description ? current.description : "Todo en kilos: lo que entra se pesa con foto del visor, se mezcla en lotes y cada parte de producción queda enganchado al lote que usó."}
      />
      {showTabs && (<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }} variant="scrollable" allowScrollButtonsMobile>
        <Tab label="Recepción" />
        <Tab label="Mezclas" />
        <Tab label="Lotes" />
        <Tab label="Recetas y orígenes" />
      </Tabs>)}
      {tab === 0 && <ReceptionTab materials={materials} onChange={loadMaterials} onTrace={id => setTrace({ kind: 'lot', id })} />}
      {tab === 1 && <MixesTab materials={materials} onChange={loadMaterials} onTrace={id => setTrace({ kind: 'mix', id })} />}
      {tab === 2 && <LotsTab materials={materials} onTrace={id => setTrace({ kind: 'lot', id })} />}
      {tab === 3 && <RecipesTab materials={materials} onChange={loadMaterials} />}
      {trace && <TraceDialog {...trace} onClose={() => setTrace(null)} onOpen={setTrace} />}
    </Box>
  );
};

export default FactoryMaterialsPage;

// ==================== Recepción ====================

type SupplierOption = { id: number; name: string };

const ReceptionTab: React.FC<{ materials: FactoryMaterial[]; onChange: () => void; onTrace: (lotId: number) => void }> = ({ materials, onChange, onTrace }) => {
  const [rows, setRows] = useState<Reception[] | null>(null);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [read, setRead] = useState<ScaleReadResult | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [form, setForm] = useState({ kg: '', materialId: null as number | null, supplierId: null as number | null, remito: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try { setRows(await api.getReceptions({ from: addDaysIso(todayIso(), -30), to: todayIso() })); }
    catch (e) { setError(errorMessage(e, 'No se pudieron cargar los ingresos')); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.getSuppliers({ page: 1, pageSize: 500 }).then(r => setSuppliers((r.suppliers || []).map((s: any) => ({ id: s.id, name: s.name })))).catch(() => setSuppliers([]));
  }, []);

  const pick = async (file?: File | null) => {
    if (!file) return;
    setError(null); setNotice(null); setReading(true); setRead(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    try {
      const r = await api.readScale(await compressPhoto(file));
      setRead(r);
      setForm(f => ({ ...f, kg: r.kg != null ? String(r.kg).replace('.', ',') : '' }));
    } catch (e) {
      setError(errorMessage(e, 'No se pudo subir la foto. Probá de nuevo o cargá los kg a mano.'));
    } finally {
      setReading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const reset = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null); setRead(null);
    setForm(f => ({ ...f, kg: '', remito: '', notes: '' }));
  };

  const save = async () => {
    const kg = num(form.kg);
    if (!form.materialId) { setError('Elegí qué materia prima entró'); return; }
    if (kg <= 0) { setError('Cargá los kg que marca el visor'); return; }
    setSaving(true); setError(null);
    try {
      const r = await api.createReception({
        materialId: form.materialId, supplierId: form.supplierId, kg, photoId: read?.photoId ?? null,
        remitoNumber: form.remito || undefined, notes: form.notes || undefined,
      });
      setNotice(`Ingreso registrado: lote ${r.lotCode}, ${fmtKg(r.kg)} de ${r.materialName}.`);
      reset();
      await load();
      onChange();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo registrar el ingreso'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: Reception) => {
    try { await api.deleteReception(r.id); await load(); onChange(); }
    catch (e) { setError(errorMessage(e, 'No se pudo anular el ingreso')); }
  };

  const ocrDiffers = read?.kg != null && num(form.kg) > 0 && Math.abs(num(form.kg) - read.kg) > 0.001;
  const today = rows?.filter(r => r.receivedAt.slice(0, 10) >= todayIso().slice(0, 10) && fmtDay(r.receivedAt) === fmtDay(new Date().toISOString())) ?? [];
  const selectedMaterial = materials.find(m => m.id === form.materialId) || null;

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 5fr) minmax(0, 6fr)' }, gap: 3, mb: 3 }}>
        {/* El visor: lo protagonista de la pantalla, pensado para el celular. */}
        <Box
          sx={{
            position: 'relative', borderRadius: 3, overflow: 'hidden', minHeight: { xs: 280, md: 380 },
            bgcolor: '#14161B', color: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          }}
        >
          {preview && (
            <>
              <Box component="img" src={preview} alt="Foto del visor" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
              {/* Degradé abajo: la foto se ve arriba y el número leído se lee sobre oscuro. */}
              <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(20,22,27,0) 25%, rgba(20,22,27,0.85) 58%, #14161B 100%)' }} />
            </>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={e => pick(e.target.files?.[0])} />
          <Box sx={{ position: 'relative', p: { xs: 2.5, md: 3.5 } }}>
            {reading ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={22} sx={{ color: '#fff' }} />
                <Typography sx={{ fontSize: 18 }}>Leyendo el visor…</Typography>
              </Stack>
            ) : read ? (
              <>
                <Typography sx={{ fontSize: 14, opacity: 0.8 }}>
                  Pesada de las {fmtTime(read.takenAt)} · hora del sistema
                </Typography>
                <Typography sx={{ fontSize: { xs: 64, md: 84 }, fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1, fontVariantNumeric: 'tabular-nums', my: 1 }}>
                  {read.kg != null ? fmtQty(read.kg) : '¿?'}<Box component="span" sx={{ fontSize: '0.35em', fontWeight: 600, ml: 1, opacity: 0.8 }}>kg</Box>
                </Typography>
                <Typography sx={{ fontSize: 14, opacity: 0.85 }}>
                  {read.kg == null ? (read.note || 'No se pudo leer: cargá los kg a mano.')
                    : read.confidence === 'alta' ? 'Lectura clara. Revisá que coincida con el visor.'
                      : `Lectura dudosa${read.note ? `: ${read.note}` : ''}. Revisá el número.`}
                </Typography>
                <Button onClick={() => fileRef.current?.click()} sx={{ mt: 2, color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }} variant="outlined" startIcon={<PhotoCamera />}>
                  Sacar otra foto
                </Button>
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.15, maxWidth: '16ch' }}>
                  Sacá una foto del visor de la balanza
                </Typography>
                <Typography sx={{ fontSize: 15, opacity: 0.75, mt: 1, maxWidth: '40ch' }}>
                  De frente y con los números enteros. El sistema lee los kilos y guarda la foto con la hora.
                </Typography>
                <Button onClick={() => fileRef.current?.click()} variant="contained" size="large" startIcon={<PhotoCamera />}
                  sx={{ mt: 3, bgcolor: '#fff', color: '#14161B', '&:hover': { bgcolor: '#E8E8E6' } }}>
                  Sacar foto
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Section title="Datos del ingreso" padded sx={{ mb: 0 }}>
          <Stack spacing={2}>
            <TextField
              label="Kilos"
              value={form.kg}
              onChange={e => setForm({ ...form, kg: e.target.value })}
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{ sx: { fontSize: 22, fontWeight: 700 } }}
              helperText={ocrDiffers ? `Cambiaste lo que se leyó (${fmtKg(read?.kg)}): queda registrado como corregido.` : read ? 'Lo que se leyó en la foto. Corregilo si no coincide.' : 'Sin foto se puede cargar igual, pero queda sin respaldo.'}
              FormHelperTextProps={{ sx: { color: ocrDiffers ? 'warning.dark' : undefined } }}
            />
            <Autocomplete
              options={materials}
              value={selectedMaterial}
              getOptionLabel={o => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, v) => setForm({ ...form, materialId: v?.id ?? null })}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                    <Box sx={{ flex: 1 }}>{o.name}</Box>
                    <OriginPill origin={o.origin} />
                  </Stack>
                </li>
              )}
              renderInput={p => <TextField {...p} label="Materia prima" required />}
            />
            {selectedMaterial && !selectedMaterial.origin && (
              <Typography variant="caption" color="warning.dark">Esta MP no tiene origen cargado. Ponelo en Recetas y orígenes para ver las mezclas por tipo.</Typography>
            )}
            <Autocomplete
              options={suppliers}
              value={suppliers.find(s => s.id === form.supplierId) || null}
              getOptionLabel={o => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, v) => setForm({ ...form, supplierId: v?.id ?? null })}
              renderInput={p => <TextField {...p} label="Proveedor" />}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Remito" value={form.remito} onChange={e => setForm({ ...form, remito: e.target.value })} fullWidth />
              <TextField label="Nota" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} fullWidth />
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {(read || preview) && <Button onClick={reset}>Descartar</Button>}
              <Button variant="contained" size="large" onClick={save} disabled={saving || reading}>
                {saving ? 'Registrando…' : 'Registrar ingreso'}
              </Button>
            </Stack>
          </Stack>
        </Section>
      </Box>

      <Section title="Ingresos de los últimos 30 días" description={today.length ? `Hoy: ${today.length} ingresos, ${fmtKg(today.reduce((a, r) => a + r.kg, 0))}` : undefined}>
        {!rows ? <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={24} /></Box> : rows.length === 0 ? (
          <EmptyState title="Todavía no hay ingresos pesados" hint="Cuando entre materia prima, sacale foto al visor desde esta pantalla: queda el lote con su foto y su hora." />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Foto</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Lote</TableCell>
                  <TableCell>Materia prima</TableCell>
                  <TableCell>Proveedor</TableCell>
                  <TableCell align="right">Kg</TableCell>
                  <TableCell>Lectura</TableCell>
                  <TableCell align="right">Queda en el lote</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ py: 0.75 }}><PhotoThumb id={r.photoId} size={36} /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.receivedAt)}</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.lotCode}{r.remitoNumber && <Typography variant="caption" color="text.secondary" display="block">Remito {r.remitoNumber}</Typography>}</TableCell>
                    <TableCell>{r.materialName}<Box sx={{ mt: 0.5 }}><OriginPill origin={r.materialOrigin} /></Box></TableCell>
                    <TableCell>{r.supplierName || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtQty(r.kg)}</TableCell>
                    <TableCell>
                      {r.ocrKg == null ? <StatusPill tone="neutral">a mano</StatusPill>
                        : r.corrected ? <Tooltip title={`La foto decía ${fmtKg(r.ocrKg)}`}><span><StatusPill tone="warn">corregido</StatusPill></span></Tooltip>
                          : <StatusPill tone="ok">foto</StatusPill>}
                    </TableCell>
                    <TableCell align="right">{fmtQty(r.lotRemainingKg)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Trazabilidad del lote"><IconButton size="small" onClick={() => onTrace(r.materialBatchId)}><Timeline fontSize="small" /></IconButton></Tooltip>
                      {r.lotRemainingKg === r.kg && (
                        <Tooltip title="Anular ingreso"><IconButton size="small" onClick={() => remove(r)}><Delete fontSize="small" /></IconButton></Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>
    </>
  );
};

// ==================== Mezclas ====================

const MixesTab: React.FC<{ materials: FactoryMaterial[]; onChange: () => void; onTrace: (mixId: number) => void }> = ({ materials, onChange, onTrace }) => {
  const [rows, setRows] = useState<MixBatch[] | null>(null);
  const [recipes, setRecipes] = useState<MixRecipe[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setRows(await api.getMixBatches({ from: addDaysIso(todayIso(), -30), to: todayIso() })); }
    catch (e) { setError(errorMessage(e, 'No se pudieron cargar las mezclas')); setRows([]); }
  }, []);
  useEffect(() => {
    load();
    api.getMixRecipes().then(setRecipes).catch(() => setRecipes([]));
    api.getMachines().then(ms => setMachines(ms.filter(m => m.kind !== 'Matriz'))).catch(() => setMachines([]));
  }, [load]);

  const remove = async (m: MixBatch) => {
    try { await api.deleteMixBatch(m.id); await load(); onChange(); }
    catch (e) { setError(errorMessage(e, 'No se pudo deshacer la mezcla')); }
  };

  const totals = useMemo(() => {
    const comps = (rows || []).flatMap(r => r.components);
    const kg = comps.reduce((a, c) => a + c.kg, 0);
    const recovered = comps.filter(c => c.materialOrigin === 'RecuperadaPropia' || c.materialOrigin === 'RecuperadaComprada').reduce((a, c) => a + c.kg, 0);
    const untracked = comps.filter(c => !c.materialBatchId).reduce((a, c) => a + c.kg, 0);
    return { kg, recovered, untracked };
  }, [rows]);

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <KpiStrip>
        <KpiItem label="Mezclado en 30 días" value={fmtKg(totals.kg)} hint={`${rows?.length ?? 0} lotes`} />
        <KpiItem label="Recuperado en las mezclas" value={totals.kg ? `${Math.round((totals.recovered / totals.kg) * 100)}%` : '—'} hint={fmtKg(totals.recovered)} />
        <KpiItem label="Sin lote de origen" value={fmtKg(totals.untracked)} tone={totals.untracked > 0 ? 'warn' : undefined}
          hint={totals.untracked > 0 ? 'Salió de stock previo, sin recepción pesada' : 'Todo rastreable hasta su ingreso'} />
      </KpiStrip>
      <Section
        title="Lotes de mezcla"
        description="Cada tanda descuenta los kg de los lotes de MP más viejos primero."
        actions={<Button variant="contained" startIcon={<Add />} onClick={() => setCreating(true)}>Armar mezcla</Button>}
      >
        {!rows ? <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={24} /></Box> : rows.length === 0 ? (
          <EmptyState title="No hay mezclas en los últimos 30 días" hint="Armá la tanda antes de cargar la inyectora: el lote de mezcla se elige después en el parte de producción." />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lote</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Receta</TableCell>
                  <TableCell>Para</TableCell>
                  <TableCell sx={{ width: 220 }}>Composición</TableCell>
                  <TableCell align="right">Kg</TableCell>
                  <TableCell align="right">Partes</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(m => (
                  <TableRow key={m.id}>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{m.code}</TableCell>
                    <TableCell>{fmtDay(m.date)}</TableCell>
                    <TableCell>{m.mixRecipeName || <Typography variant="body2" color="text.secondary">a mano</Typography>}</TableCell>
                    <TableCell>{[m.machineName, m.productName].filter(Boolean).join(' · ') || '—'}</TableCell>
                    <TableCell><CompositionBar parts={m.components.map(c => ({ origin: c.materialOrigin, kg: c.kg }))} /></TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtQty(m.totalKg)}</TableCell>
                    <TableCell align="right">{m.shiftCount || '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Trazabilidad"><IconButton size="small" onClick={() => onTrace(m.id)}><Timeline fontSize="small" /></IconButton></Tooltip>
                      {m.shiftCount === 0 && <Tooltip title="Deshacer mezcla (devuelve los kg)"><IconButton size="small" onClick={() => remove(m)}><Delete fontSize="small" /></IconButton></Tooltip>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>
      {creating && (
        <MixDialog materials={materials} recipes={recipes.filter(r => r.isActive)} machines={machines}
          onClose={() => setCreating(false)} onSaved={async () => { setCreating(false); await load(); onChange(); }} />
      )}
    </>
  );
};

const MixDialog: React.FC<{ materials: FactoryMaterial[]; recipes: MixRecipe[]; machines: Machine[]; onClose: () => void; onSaved: () => void }> = ({ materials, recipes, machines, onClose, onSaved }) => {
  const [recipeId, setRecipeId] = useState<number | ''>(recipes[0]?.id ?? '');
  const [totalKg, setTotalKg] = useState('');
  const [lines, setLines] = useState<{ materialId: number | null; kg: string }[]>([{ materialId: null, kg: '' }]);
  const [machineId, setMachineId] = useState<number | ''>('');
  const [productId, setProductId] = useState<number | null>(null);
  const [date, setDate] = useState(todayIso());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const recipe = recipes.find(r => r.id === recipeId);
  const manual = recipeId === '';

  const preview = manual
    ? lines.filter(l => l.materialId && num(l.kg) > 0).map(l => ({ materialId: l.materialId!, kg: num(l.kg) }))
    : (recipe?.lines || []).map(l => ({ materialId: l.materialId, kg: Math.round(num(totalKg) * l.pct) / 100 }));
  const short = preview.map(p => ({ ...p, m: materials.find(m => m.id === p.materialId) })).filter(p => p.m && p.kg > p.m.currentStock);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await api.createMixBatch({
        date, mixRecipeId: manual ? null : recipeId as number, totalKg: manual ? null : num(totalKg),
        lines: manual ? preview : undefined, machineId: machineId === '' ? null : machineId, productId,
      });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo armar la mezcla'));
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Armar mezcla</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select label="Receta" value={recipeId} onChange={e => setRecipeId(e.target.value === '' ? '' : Number(e.target.value))} fullWidth>
              {recipes.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
              <MenuItem value="">Sin receta: cargo los kg de cada MP</MenuItem>
            </TextField>
            <TextField type="date" label="Fecha" value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 170 }} />
          </Stack>
          {!manual ? (
            <TextField label="Kg totales de la tanda" value={totalKg} onChange={e => setTotalKg(e.target.value)} inputProps={{ inputMode: 'decimal' }} autoFocus />
          ) : (
            <Stack spacing={1}>
              {lines.map((l, i) => (
                <Stack key={i} direction="row" spacing={1}>
                  <TextField select label="Materia prima" value={l.materialId ?? ''} onChange={e => setLines(ls => ls.map((x, j) => (j === i ? { ...x, materialId: Number(e.target.value) } : x)))} sx={{ flex: 1 }}>
                    {materials.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                  </TextField>
                  <TextField label="Kg" value={l.kg} onChange={e => setLines(ls => ls.map((x, j) => (j === i ? { ...x, kg: e.target.value } : x)))} sx={{ width: 120 }} inputProps={{ inputMode: 'decimal' }} />
                  <IconButton onClick={() => setLines(ls => ls.filter((_, j) => j !== i))} disabled={lines.length === 1}><Delete fontSize="small" /></IconButton>
                </Stack>
              ))}
              <Button size="small" startIcon={<Add />} onClick={() => setLines(ls => [...ls, { materialId: null, kg: '' }])} sx={{ alignSelf: 'flex-start' }}>Agregar MP</Button>
            </Stack>
          )}
          {preview.length > 0 && preview.some(p => p.kg > 0) && (
            <Box sx={{ bgcolor: '#F7F7F5', borderRadius: 2, p: 2 }}>
              <CompositionBar height={10} parts={preview.map(p => ({ origin: materials.find(m => m.id === p.materialId)?.origin, kg: p.kg }))} />
              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                {preview.map(p => {
                  const m = materials.find(x => x.id === p.materialId);
                  return (
                    <Stack key={p.materialId} direction="row" justifyContent="space-between">
                      <Typography variant="body2">{m?.name}</Typography>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtKg(p.kg)}</Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          )}
          {short.length > 0 && (
            <Alert severity="warning">
              No alcanza el stock de {short.map(s => `${s.m!.name} (hay ${fmtKg(s.m!.currentStock)})`).join(', ')}. Se puede registrar igual, pero el stock va a quedar negativo.
            </Alert>
          )}
          <Divider />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select label="Inyectora" value={machineId} onChange={e => setMachineId(e.target.value === '' ? '' : Number(e.target.value))} fullWidth>
              <MenuItem value="">Sin definir</MenuItem>
              {machines.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
            </TextField>
            <Box sx={{ flex: 1 }}><ProductSelect value={productId} onChange={id => setProductId(id)} size="medium" label="Producto" /></Box>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving || preview.every(p => p.kg <= 0)}>{saving ? 'Armando…' : 'Armar mezcla'}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== Lotes de MP ====================

const LotsTab: React.FC<{ materials: FactoryMaterial[]; onTrace: (lotId: number) => void }> = ({ materials, onTrace }) => {
  const [materialId, setMaterialId] = useState<number | ''>('');
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [rows, setRows] = useState<MaterialLot[] | null>(null);
  useEffect(() => {
    setRows(null);
    api.getMaterialLots({ materialId: materialId === '' ? undefined : materialId, onlyAvailable }).then(setRows).catch(() => setRows([]));
  }, [materialId, onlyAvailable]);
  const untracked = materials.filter(m => m.currentStock - m.lotsKg > 0.5);

  return (
    <>
      <KpiStrip>
        <KpiItem label="Kg en lotes" value={fmtKg(materials.reduce((a, m) => a + m.lotsKg, 0))} hint={`${materials.reduce((a, m) => a + m.openLots, 0)} lotes con saldo`} />
        <KpiItem label="Kg sin lote" value={fmtKg(untracked.reduce((a, m) => a + (m.currentStock - m.lotsKg), 0))}
          tone={untracked.length ? 'warn' : undefined} hint={untracked.length ? 'Stock de antes de pesar los ingresos' : 'Todo el stock tiene lote'} />
      </KpiStrip>
      <Section
        title="Lotes de materia prima"
        actions={
          <Stack direction="row" spacing={1}>
            <TextField select size="small" label="MP" value={materialId} onChange={e => setMaterialId(e.target.value === '' ? '' : Number(e.target.value))} sx={{ minWidth: 200 }}>
              <MenuItem value="">Todas</MenuItem>
              {materials.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Mostrar" value={onlyAvailable ? 's' : 'n'} onChange={e => setOnlyAvailable(e.target.value === 's')} sx={{ minWidth: 140 }}>
              <MenuItem value="s">Con saldo</MenuItem>
              <MenuItem value="n">Todos</MenuItem>
            </TextField>
          </Stack>
        }
      >
        {!rows ? <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={24} /></Box> : rows.length === 0 ? (
          <EmptyState title="No hay lotes" hint="Los lotes se crean solos con cada recepción pesada." />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lote</TableCell>
                  <TableCell>Materia prima</TableCell>
                  <TableCell>Proveedor</TableCell>
                  <TableCell>Ingresó</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(l => (
                  <TableRow key={l.id}>
                    <TableCell sx={{ fontWeight: 600 }}>{l.lotCode}</TableCell>
                    <TableCell>{l.materialName}</TableCell>
                    <TableCell>{l.supplierName || '—'}</TableCell>
                    <TableCell>{fmtDateTime(l.createdAt)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtKg(l.remainingKg)}</TableCell>
                    <TableCell align="right"><Tooltip title="Trazabilidad"><IconButton size="small" onClick={() => onTrace(l.id)}><Timeline fontSize="small" /></IconButton></Tooltip></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>
    </>
  );
};

// ==================== Recetas y orígenes ====================

const RecipesTab: React.FC<{ materials: FactoryMaterial[]; onChange: () => void }> = ({ materials, onChange }) => {
  const [recipes, setRecipes] = useState<MixRecipe[] | null>(null);
  const [editing, setEditing] = useState<Partial<MixRecipe> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => setRecipes(await api.getMixRecipes()), []);
  useEffect(() => { load().catch(() => setRecipes([])); }, [load]);

  const setOrigin = async (m: FactoryMaterial, origin: string) => {
    try { await api.setMaterialOrigin(m.id, (origin || null) as MaterialOrigin | null); onChange(); }
    catch (e) { setError(errorMessage(e, 'No se pudo guardar el origen')); }
  };
  const remove = async (r: MixRecipe) => {
    try { await api.deleteMixRecipe(r.id); await load(); }
    catch (e) { setError(errorMessage(e, 'No se pudo borrar la receta')); }
  };

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>
        <Section
          title="Recetas de mezcla"
          description="Qué lleva cada mezcla, en porcentaje."
          actions={<Button variant="contained" startIcon={<Add />} onClick={() => setEditing({ name: '', isActive: true, lines: [] })}>Nueva receta</Button>}
        >
          {!recipes ? <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={24} /></Box> : recipes.length === 0 ? (
            <EmptyState title="Sin recetas" hint="Por ejemplo: 70% PP virgen y 30% recuperado propio. Con la receta, armar una tanda es cargar solo los kg totales." />
          ) : (
            <Box>
              {recipes.map(r => (
                <Box key={r.id} sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 }, opacity: r.isActive ? 1 : 0.55 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={{ fontWeight: 650, flex: 1 }}>{r.name}{!r.isActive && ' (desactivada)'}</Typography>
                    <IconButton size="small" onClick={() => setEditing(r)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => remove(r)}><Delete fontSize="small" /></IconButton>
                  </Stack>
                  <Box sx={{ my: 1 }}><CompositionBar parts={r.lines.map(l => ({ origin: l.materialOrigin, kg: l.pct }))} /></Box>
                  <Typography variant="body2" color="text.secondary">
                    {r.lines.map(l => `${l.materialName} ${fmtQty(l.pct)}%`).join(', ')}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Section>

        <Section title="Origen de cada materia prima" description="Virgen, recuperada propia (de rotos y colada) o recuperada comprada.">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Materia prima</TableCell>
                  <TableCell align="right">Stock</TableCell>
                  <TableCell sx={{ width: 220 }}>Origen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {materials.map(m => (
                  <TableRow key={m.id}>
                    <TableCell>{m.name}</TableCell>
                    <TableCell align="right">{fmtQty(m.currentStock)} {m.unit || ''}</TableCell>
                    <TableCell>
                      <TextField select size="small" fullWidth value={m.origin || ''} onChange={e => setOrigin(m, e.target.value)}
                        SelectProps={{ renderValue: v => <OriginPill origin={v as string} /> , displayEmpty: true }}>
                        <MenuItem value="">Sin origen</MenuItem>
                        {ORIGINS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </TextField>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>
      </Box>
      {editing && <RecipeDialog recipe={editing} materials={materials} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
    </>
  );
};

const RecipeDialog: React.FC<{ recipe: Partial<MixRecipe>; materials: FactoryMaterial[]; onClose: () => void; onSaved: () => void }> = ({ recipe, materials, onClose, onSaved }) => {
  const [name, setName] = useState(recipe.name || '');
  const [lines, setLines] = useState<{ materialId: number | null; pct: string }[]>(
    recipe.lines?.length ? recipe.lines.map(l => ({ materialId: l.materialId, pct: String(l.pct) })) : [{ materialId: null, pct: '' }, { materialId: null, pct: '' }],
  );
  const [error, setError] = useState<string | null>(null);
  const total = lines.reduce((a, l) => a + num(l.pct), 0);

  const save = async () => {
    setError(null);
    try {
      await api.saveMixRecipe({
        id: recipe.id, name, notes: recipe.notes, isActive: recipe.isActive ?? true,
        lines: lines.filter(l => l.materialId).map(l => ({ materialId: l.materialId!, pct: num(l.pct) })),
      });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar la receta'));
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{recipe.id ? 'Editar receta' : 'Nueva receta de mezcla'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Nombre" value={name} onChange={e => setName(e.target.value)} placeholder="Tapón 70/30" autoFocus />
          {lines.map((l, i) => (
            <Stack key={i} direction="row" spacing={1}>
              <TextField select label="Materia prima" value={l.materialId ?? ''} sx={{ flex: 1 }}
                onChange={e => setLines(ls => ls.map((x, j) => (j === i ? { ...x, materialId: Number(e.target.value) } : x)))}>
                {materials.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
              </TextField>
              <TextField label="%" value={l.pct} sx={{ width: 100 }} inputProps={{ inputMode: 'decimal' }}
                onChange={e => setLines(ls => ls.map((x, j) => (j === i ? { ...x, pct: e.target.value } : x)))} />
              <IconButton onClick={() => setLines(ls => ls.filter((_, j) => j !== i))} disabled={lines.length === 1}><Delete fontSize="small" /></IconButton>
            </Stack>
          ))}
          <Stack direction="row" alignItems="center">
            <Button size="small" startIcon={<Add />} onClick={() => setLines(ls => [...ls, { materialId: null, pct: '' }])}>Agregar MP</Button>
            <Box sx={{ flex: 1 }} />
            <StatusPill tone={Math.abs(total - 100) <= 0.5 ? 'ok' : 'warn'}>Suma {fmtQty(total)}%</StatusPill>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={!name.trim() || Math.abs(total - 100) > 0.5}>Guardar receta</Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== Trazabilidad ====================

const ShiftList: React.FC<{ shifts: TraceShift[] }> = ({ shifts }) => (
  shifts.length === 0 ? <Typography variant="body2" color="text.secondary">Ningún parte usó este lote todavía.</Typography> : (
    <Stack spacing={0.75}>
      {shifts.map(s => (
        <Stack key={s.id} direction="row" spacing={1.5} alignItems="baseline">
          <Typography variant="body2" sx={{ minWidth: 70, color: 'text.secondary' }}>{fmtDay(s.date)} {s.shift}</Typography>
          <Typography variant="body2" sx={{ flex: 1 }}>{s.productName}{s.machineName ? ` · ${s.machineName}` : ''}{s.operatorName ? ` · ${s.operatorName}` : ''}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtQty(s.goodQuantity)} u.</Typography>
        </Stack>
      ))}
    </Stack>
  )
);

/** Un eslabón de la cadena: etiqueta a la izquierda, contenido a la derecha, línea que los une. */
const Link: React.FC<{ label: string; last?: boolean; children: React.ReactNode }> = ({ label, last, children }) => (
  <Stack direction="row" spacing={2}>
    <Stack alignItems="center" sx={{ width: 14, pt: 0.75 }}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
      {!last && <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', my: 0.5 }} />}
    </Stack>
    <Box sx={{ flex: 1, pb: last ? 0 : 3, minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{label}</Typography>
      {children}
    </Box>
  </Stack>
);

export const TraceDialog: React.FC<{ kind: 'mix' | 'lot'; id: number; onClose: () => void; onOpen?: (t: { kind: 'mix' | 'lot'; id: number }) => void }> = ({ kind, id, onClose, onOpen }) => {
  const [mix, setMix] = useState<MixTrace | null>(null);
  const [lot, setLot] = useState<LotTrace | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setMix(null); setLot(null); setError(null);
    (kind === 'mix' ? api.traceMix(id).then(setMix) : api.traceLot(id).then(setLot)).catch(e => setError(errorMessage(e, 'No se pudo cargar la trazabilidad')));
  }, [kind, id]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {kind === 'mix' ? `Mezcla ${mix?.mix.code ?? ''}` : `Lote ${lot?.lot.lotCode ?? ''}`}
        <Typography variant="body2" color="text.secondary">
          {kind === 'mix' ? 'De dónde salió cada kilo y qué se fabricó con esta mezcla.' : 'Cómo entró este lote y en qué terminó.'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!error && !mix && !lot && <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
        {mix && (
          <Box sx={{ pt: 1 }}>
            <Link label="Recepciones de materia prima">
              <Stack spacing={1.25}>
                {mix.mix.components.map((c, i) => (
                  <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                    <PhotoThumb id={c.photoId} size={44} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.materialBatchId && onOpen
                          ? <Box component="button" type="button" onClick={() => onOpen({ kind: 'lot', id: c.materialBatchId! })} sx={{ all: 'unset', cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>{c.lotCode}</Box>
                          : 'Sin lote'} · {c.materialName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.materialBatchId ? `${c.supplierName || 'Sin proveedor'} · ingresó ${fmtDateTime(c.receivedAt)}` : 'Stock de antes de pesar los ingresos'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtKg(c.kg)}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Link>
            <Link label={`Mezcla del ${fmtDay(mix.mix.date)}${mix.mix.mixRecipeName ? ` · ${mix.mix.mixRecipeName}` : ''}`}>
              <CompositionBar height={10} parts={mix.mix.components.map(c => ({ origin: c.materialOrigin, kg: c.kg }))} />
              <Typography variant="body2" sx={{ mt: 1 }}>{fmtKg(mix.mix.totalKg)}{mix.mix.machineName ? ` para ${mix.mix.machineName}` : ''}</Typography>
            </Link>
            <Link label="Partes de producción" last><ShiftList shifts={mix.shifts} /></Link>
          </Box>
        )}
        {lot && (
          <Box sx={{ pt: 1 }}>
            <Link label="Recepción">
              {lot.reception ? (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <PhotoThumb id={lot.reception.photoId} size={56} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtKg(lot.reception.kg)} de {lot.reception.materialName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {lot.reception.supplierName || 'Sin proveedor'}{lot.reception.remitoNumber ? ` · remito ${lot.reception.remitoNumber}` : ''} · {fmtDateTime(lot.reception.receivedAt)}
                    </Typography>
                    {lot.reception.createdByName && <Typography variant="caption" color="text.secondary">Recibió {lot.reception.createdByName}</Typography>}
                  </Box>
                </Stack>
              ) : <Typography variant="body2" color="text.secondary">Lote cargado sin recepción pesada.</Typography>}
            </Link>
            <Link label={`Mezclas (queda ${fmtKg(lot.lot.remainingKg)} en el lote)`} last={lot.mixes.length === 0}>
              {lot.mixes.length === 0 ? <Typography variant="body2" color="text.secondary">Todavía no se usó.</Typography> : (
                <Stack spacing={2}>
                  {lot.mixes.map(m => (
                    <Box key={m.mixBatchId}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {onOpen
                          ? <Box component="button" type="button" onClick={() => onOpen({ kind: 'mix', id: m.mixBatchId })} sx={{ all: 'unset', cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>{m.code}</Box>
                          : m.code} · {fmtDay(m.date)} · {fmtKg(m.kgFromLot)} de {fmtKg(m.totalKg)}
                      </Typography>
                      <ShiftList shifts={m.shifts} />
                    </Box>
                  ))}
                </Stack>
              )}
            </Link>
          </Box>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
    </Dialog>
  );
};
